import { FlatfileRecord } from '@flatfile/plugin-record-hook';
import type { VisitorManagementFeatureProperties } from 'application-types';
import { isRequired } from 'lane-shared/domains/visitorManagement/features/VisitorManagement';
import { VISITOR_CUSTOM_FIELD_TYPE_VALUES } from 'constants-visitor-management';
import type { FormCustomFieldColumn } from './workbook';

import { validateCustomFieldValue } from '../validators/validateCustomFieldValue';

// i18next is not initialized in the Flatfile listener context; supply static English fallbacks.
const FLATFILE_TRANSLATIONS: Record<string, string> = {
  'web.admin.visitorManagement.customField.form.validation.fieldLimitExceeded':
    'This field cannot exceed 100 characters.',
};

function translateForFlatfile(key: string, params?: Record<string, string>): string {
  const template = FLATFILE_TRANSLATIONS[key] ?? key;

  if (!params) {
    return template;
  }

  return template.replace(/{{\s*(\w+)\s*}}/g, (_match, paramName) => params[paramName] ?? '');
}

const MULTI_SELECT_VALUE_DELIMITER = /[,|;]/;

/**
 * `enum-list` matching (unlike `enum`) requires an exact match against a configured option's
 * `value` — it does not fuzzy-match against the display label the way single-select `enum` does.
 * Resolve a raw CSV token (which is typically the display label, e.g. "Option 1") to its
 * canonical option value (e.g. "option_1") so Flatfile's stricter enum-list matcher accepts it.
 * Falls back to the raw option text, trimmed, when no configured option matches.
 */
function resolveToOptionValue(
  rawOptionText: string,
  options: FormCustomFieldColumn['options']
): string {
  const trimmed = rawOptionText.trim();

  if (!options?.length) {
    return trimmed;
  }

  const normalized = trimmed.toLowerCase();
  const match = options.find(
    option =>
      option.value?.trim().toLowerCase() === normalized ||
      option.displayText?.trim().toLowerCase() === normalized
  );

  return match ? match.value : trimmed;
}

/**
 * Flatfile's `enum-list` CSV ingestion does not split a delimited cell (e.g. "Option 1,Option 2")
 * into multiple values — it matches the whole string as one candidate and marks it invalid. Per
 * Flatfile's documented workaround, split it back into a real array ourselves before validation:
 * https://support.flatfile.com/articles/4443901963-using-string-list-and-enum-list-field-types-for-multi-value-fields
 * Each split token is then resolved to its canonical option value (see resolveToOptionValue)
 * since enum-list matching needs an exact value match, not a label match.
 */
function splitMultiSelectCellValues(
  record: FlatfileRecord,
  formCustomFields: FormCustomFieldColumn[]
): void {
  for (const field of formCustomFields) {
    if (field.fieldType !== VISITOR_CUSTOM_FIELD_TYPE_VALUES.MULTI_SELECT) {
      continue;
    }

    const cellValue = record.get(field.fieldKey) as unknown;

    if (!Array.isArray(cellValue)) {
      continue;
    }

    const resolvedValues = cellValue
      .flatMap(item =>
        typeof item === 'string' ? item.split(MULTI_SELECT_VALUE_DELIMITER) : [item]
      )
      .map(item =>
        typeof item === 'string' ? resolveToOptionValue(item, field.options) : item
      )
      .filter(item => item !== '' && item !== null && item !== undefined);

    const hasChanged =
      resolvedValues.length !== cellValue.length ||
      resolvedValues.some((value, index) => value !== cellValue[index]);

    if (hasChanged) {
      // `FlatfileRecord.set`'s installed type only declares string | boolean | number | null,
      // but Flatfile's own docs confirm passing an array here is how enum-list cells are rewritten.
      (record.set as (field: string, value: unknown) => void)(field.fieldKey, resolvedValues);
    }
  }
}

/**
 * Form custom fields: truly empty cells are skipped here because Flatfile's required column
 * constraint handles them (avoids duplicate messages). Whitespace-only values are NOT skipped —
 * they must be validated so the required error surfaces.
 */
function validateFormCustomFieldValues(
  record: FlatfileRecord,
  formCustomFields: FormCustomFieldColumn[]
): void {
  for (const field of formCustomFields) {
    const cellValue = record.get(field.fieldKey);
    const isTrulyEmpty =
      cellValue === undefined ||
      cellValue === null ||
      (Array.isArray(cellValue) ? cellValue.length === 0 : String(cellValue) === '');

    if (isTrulyEmpty) {
      continue;
    }

    // multi_select cells from the enum-list picker arrive as an array; join to reuse the
    // same comma-delimited parsing as pasted-string input in validateOptionValue.
    const value = Array.isArray(cellValue)
      ? cellValue.map(String).join(',')
      : String(cellValue).trim();

    const error = validateCustomFieldValue(
      {
        fieldKey: field.fieldKey,
        fieldName: field.fieldName,
        fieldType: field.fieldType,
        options: field.options,
      },
      value,
      {
        tFn: translateForFlatfile,
        invalidOptionMessage: 'Invalid Input',
      }
    );

    if (error) {
      record.addError(field.fieldKey, error);
    }
  }
}

export const validateRecord = (
  record: FlatfileRecord,
  props: VisitorManagementFeatureProperties,
  formCustomFields: FormCustomFieldColumn[] = [],
  /** When true, runs format/length/option rules via validateCustomFieldValue (gated by VM custom-fields flag at call site). */
  enableCustomFieldValueValidation = false,
  /** Emails already accepted from a previously submitted CSV in this session. */
  existingEmails: Set<string> = new Set()
) => {
  if (formCustomFields.length > 0) {
    splitMultiSelectCellValues(record, formCustomFields);
  }

  const email = record.get('email');
  const countryCode = record.get('countryCode');
  const cellNumber = record.get('cellNumber');
  const company = record.get('company');

  // Validate required fields
  if (isRequired(props.emailSetting) && !email) {
    record.addError('email', 'Email is required');
  }

  if (isRequired(props.phoneSetting) && !cellNumber) {
    record.addError('cellNumber', 'Phone is required');
  }

  if (isRequired(props.companySetting) && !company) {
    record.addError('company', 'Company is required');
  }

  // Validate email format
  const emailRegex = /^(?!.*\.{2})[\p{L}\p{M}\p{N}._%+-]+@[\p{L}\p{M}\p{N}.-]+\.[\p{L}\p{M}]{2,63}$/u;

  if (email && !emailRegex.test(email as string)) {
    record.addError('email', 'is not formatted like an email');
  }

  if (email && existingEmails.has((email as string).trim().toLowerCase())) {
    record.addError('email', 'This email is already in use for another visitor. Enter a different email.');
  }

  // Validate phone number format
  const fourteenDigitNumberRegex = /^[1-9]{1}[0-9]{9,13}$/;

  if (cellNumber && !fourteenDigitNumberRegex.test(cellNumber as string)) {
    record.addError(
      'cellNumber',
      'Cell number can not have special characters, spaces, or be less than 10 digits or more than 14 digits'
    );
  }

  // Validate country code format
  const threeDigitNumberRegex = /^[1-9]{1}[0-9]{0,2}$/;

  if (countryCode && !threeDigitNumberRegex.test(countryCode as string)) {
    record.addError(
      'countryCode',
      'Country code can not start with 0, be more than 3 digits, or have special characters'
    );
  }

  // Ensure country code and phone number are both present or both absent
  if ((!countryCode && cellNumber) || (countryCode && !cellNumber)) {
    record.addError(
      ['countryCode', 'cellNumber'],
      'Either both Country Code and Cell Number must be present or both must be absent'
    );
  }

  if (enableCustomFieldValueValidation) {
    validateFormCustomFieldValues(record, formCustomFields);
  }

  return record;
};
